import { FC, useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useBlocker } from 'react-router-dom';
import { Button } from 'react-daisyui';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperType } from 'swiper';
import { Navigation } from 'swiper/modules';
import { Document, Page } from 'react-pdf';
import PageHeader from '@/components/ui/PageHeader/PageHeader.tsx';
import usePastPapers from '@/api/usePastPapers.ts';
import usePaperQuestions, { QuestionWithOrder } from '@/api/usePaperQuestions.ts';
import usePaperAttempt from '@/api/usePaperAttempt.ts';
import useAttemptAnswers from '@/api/useAttemptAnswers.ts';
import useUpdateAnswer from '@/api/useUpdateAnswer.ts';
import useSubmitAttempt from '@/api/useSubmitAttempt.ts';
import supabaseClient from '@/api/supabaseClient.ts';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import QuestionContentArea from '@/components/ui/QuestionContentArea/QuestionContentArea';
import ExitPaperAttemptButton from '@/components/ui/ExitPaperAttemptButton/ExitPaperAttemptButton';
import ReadingInsertButton from '@/components/ui/ReadingInsertButton/ReadingInsertButton';
import ReadingInsertModal from '@/components/ui/ReadingInsertModal/ReadingInsertModal';
import MarkdownContent from '@/components/ui/MarkdownContent/MarkdownContent';

import 'swiper/css';
import 'swiper/css/navigation';
import { formatPastPaperTitle } from '@/utils/formatPastPaperTitle';
import { getPaperConfig, PaperConfig, QuestionCategory } from '@/_constants/paperConfigs';

interface SectionIntroSlide {
  type: 'section-intro';
  section: 'A' | 'B';
  title: string;
  subtitle: string;
  instructions: string[];
}

interface QuestionSlide {
  type: 'question';
  questionId: string;
  questionIndex: number;
  question: QuestionWithOrder;
}

interface SectionBSlide {
  type: 'section-b';
  questions: QuestionWithOrder[];
}

type Slide = SectionIntroSlide | QuestionSlide | SectionBSlide;

interface ParsedQuestionContent {
  hasExtract: boolean;
  questionNumber: string;
  extractInstruction: string;
  extractText: string;
  mainQuestion: string;
  referencedLines: { start: number; end: number } | null;
}

const TakePaperPage: FC = () => {
  const { attemptId } = useParams<{ attemptId: string }>();
  const navigate = useNavigate();
  const swiperRef = useRef<SwiperType | null>(null);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({}); // questionId -> answer text
  const [selectedSectionBQuestion, setSelectedSectionBQuestion] = useState<string | null>(null); // questionId of selected Section B question
  const [showReadingInsert, setShowReadingInsert] = useState(false);
  // Dual reading insert modals for side-by-side viewing (synthesis questions)
  const [showReadingInsertText1, setShowReadingInsertText1] = useState(false);
  const [showReadingInsertText2, setShowReadingInsertText2] = useState(false);
  const [isQuestionCollapsed, setIsQuestionCollapsed] = useState(false);
  const [showTimeUpModal, setShowTimeUpModal] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showNavigationWarning, setShowNavigationWarning] = useState(false);
  const [isReviewMode, setIsReviewMode] = useState(false);
  const saveTimeoutRef = useRef<Record<string, NodeJS.Timeout>>({});

  // Fetch the attempt
  const { data: attempt, isLoading: isLoadingAttempt } = usePaperAttempt(attemptId);

  // Fetch existing answers for this attempt
  const { data: existingAnswers = [] } = useAttemptAnswers(attemptId);

  // Update answer mutation
  const updateAnswer = useUpdateAnswer();

  // Submit attempt mutation
  const submitAttempt = useSubmitAttempt();

  // Fetch past papers
  const { data: pastPapers = [] } = usePastPapers();

  // Block navigation away from exam while in progress
  const blocker = useBlocker(
    useCallback(() => attempt?.status === 'in_progress' && !isReviewMode, [attempt?.status, isReviewMode])
  );

  // Redirect to results if attempt is already submitted
  useEffect(() => {
    if (attempt && attempt.status !== 'in_progress') {
      navigate(`/paper-attempts/${attemptId}/results`, { replace: true });
    }
  }, [attempt, attemptId, navigate]);

  // Find the selected paper from the attempt
  const paper = useMemo(() => {
    if (!attempt) return undefined;
    return pastPapers.find((p) => p.id === attempt.past_paper_id);
  }, [pastPapers, attempt]);

  // Fetch questions for this paper
  const { data: questions = [], isLoading: isLoadingQuestions } = usePaperQuestions(attempt?.past_paper_id);

  // Get paper configuration based on paper code
  const paperConfig = useMemo((): PaperConfig | undefined => {
    return getPaperConfig(paper?.paper_code);
  }, [paper?.paper_code]);

  // Load existing answers when they're fetched
  useEffect(() => {
    if (existingAnswers.length > 0 && paperConfig) {
      const answersMap: Record<string, string> = {};
      let choiceSectionQuestionId: string | null = null;

      // Get categories that belong to choice sections (like Section B)
      const choiceSectionCategories = paperConfig.sections
        .filter((s) => s.hasQuestionChoice)
        .flatMap((s) => s.categories);

      existingAnswers.forEach((answer) => {
        if (answer.answer_text) {
          answersMap[answer.question_id] = answer.answer_text;

          // Check if this is a choice section question
          const question = questions.find((q) => q.id === answer.question_id);
          if (question && choiceSectionCategories.includes(question.category as QuestionCategory)) {
            choiceSectionQuestionId = answer.question_id;
          }
        }
      });

      // Only update state if values have actually changed
      setAnswers((prev) => {
        const hasChanged =
          Object.keys(answersMap).length !== Object.keys(prev).length ||
          Object.keys(answersMap).some((key) => answersMap[key] !== prev[key]);
        return hasChanged ? answersMap : prev;
      });

      if (choiceSectionQuestionId) {
        setSelectedSectionBQuestion((prev) => (prev !== choiceSectionQuestionId ? choiceSectionQuestionId : prev));
      }
    }
  }, [existingAnswers, questions, paperConfig]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(saveTimeoutRef.current).forEach(clearTimeout);
    };
  }, []);

  // Warn user before closing browser/tab during active exam
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Only show warning if exam is still in progress (not in review mode or submitted)
      if (attempt?.status === 'in_progress' && !isReviewMode) {
        e.preventDefault();
        // Modern browsers ignore custom messages, but setting returnValue triggers the warning
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [attempt?.status, isReviewMode]);

  // Show navigation warning modal when blocker is triggered
  useEffect(() => {
    if (blocker.state === 'blocked') {
      setShowNavigationWarning(true);
    }
  }, [blocker.state]);

  // Build slides array: section intros + questions
  const slides = useMemo((): Slide[] => {
    if (questions.length === 0 || !paperConfig) return [];

    const slideArray: Slide[] = [];

    // Build slides from paper configuration sections
    paperConfig.sections.forEach((sectionConfig) => {
      // Add section intro slide
      slideArray.push({
        type: 'section-intro',
        section: sectionConfig.id as 'A' | 'B',
        title: sectionConfig.title,
        subtitle: sectionConfig.subtitle,
        instructions: sectionConfig.instructions,
      });

      // Get questions for this section
      const sectionQuestions = questions.filter((q) =>
        sectionConfig.categories.includes(q.category as QuestionCategory)
      );

      if (sectionConfig.hasQuestionChoice) {
        // For sections with question choice (like Section B), show all questions on one slide
        if (sectionQuestions.length > 0) {
          slideArray.push({
            type: 'section-b',
            questions: sectionQuestions,
          });
        }
      } else {
        // For regular sections, add each question as a separate slide
        sectionQuestions.forEach((question, index) => {
          slideArray.push({
            type: 'question',
            questionId: question.id,
            questionIndex: index,
            question,
          });
        });
      }
    });

    return slideArray;
  }, [questions, paperConfig]);

  // Generate PDF URL
  const pdfUrl = useMemo(() => {
    if (!paper?.pdf_url) return '';
    const { data } = supabaseClient.storage.from('past-papers').getPublicUrl(paper.pdf_url);
    return data.publicUrl;
  }, [paper]);

  // Handle slide change
  const handleSlideChange = (swiper: SwiperType) => {
    const prevSlide = slides[swiper.previousIndex];

    // Save answer immediately when leaving a question
    if (prevSlide && prevSlide.type === 'question' && attemptId) {
      const questionId = prevSlide.questionId;
      const answerText = answers[questionId] || '';

      // Clear any pending timeout
      if (saveTimeoutRef.current[questionId]) {
        clearTimeout(saveTimeoutRef.current[questionId]);
      }

      // Save immediately
      updateAnswer.mutate({
        attemptId,
        questionId,
        answerText,
      });
    }

    // Save Section B answer when leaving Section B slide
    if (prevSlide && prevSlide.type === 'section-b' && attemptId && selectedSectionBQuestion) {
      const answerText = answers[selectedSectionBQuestion] || '';

      // Clear any pending timeout
      if (saveTimeoutRef.current[selectedSectionBQuestion]) {
        clearTimeout(saveTimeoutRef.current[selectedSectionBQuestion]);
      }

      // Save immediately
      updateAnswer.mutate({
        attemptId,
        questionId: selectedSectionBQuestion,
        answerText,
      });
    }

    // Reset question collapsed state when changing slides
    setIsQuestionCollapsed(false);

    // Update current slide index
    setCurrentSlideIndex(swiper.activeIndex);
  };

  // Get current answer for the active question
  const getCurrentAnswer = (): string => {
    const currentSlide = slides[currentSlideIndex];
    if (currentSlide && currentSlide.type === 'question') {
      return answers[currentSlide.questionId] || '';
    }
    if (currentSlide && currentSlide.type === 'section-b' && selectedSectionBQuestion) {
      return answers[selectedSectionBQuestion] || '';
    }
    return '';
  };

  // Parse question content to separate extract from main question and detect line references
  const parseQuestionContent = (content: string): ParsedQuestionContent => {
    if (!content) {
      return {
        hasExtract: false,
        questionNumber: '',
        extractInstruction: '',
        extractText: '',
        mainQuestion: '',
        referencedLines: null,
      };
    }

    // Check for line references (e.g., "lines 1-3", "from lines 7 to 17", "line 5")
    const lineReferencePatterns = [
      /(?:from\s+)?lines?\s+(\d+)[-–]\s*(\d+)/i, // "lines 1-3" or "from lines 7-17"
      /(?:from\s+)?lines?\s+(\d+)\s+to\s+(\d+)/i, // "lines 1 to 3"
      /line\s+(\d+)/i, // "line 5" (single line)
    ];

    let referencedLines: { start: number; end: number } | null = null;
    for (const pattern of lineReferencePatterns) {
      const match = content.match(pattern);
      if (match) {
        const start = parseInt(match[1], 10);
        const end = match[2] ? parseInt(match[2], 10) : start; // Single line if no end specified
        referencedLines = { start, end };
        break;
      }
    }

    // Check for extract markers added by OpenAI
    const extractStartMarker = '<<<EXTRACT_START>>>';
    const extractEndMarker = '<<<EXTRACT_END>>>';

    if (content.includes(extractStartMarker) && content.includes(extractEndMarker)) {
      const startIndex = content.indexOf(extractStartMarker);
      const endIndex = content.indexOf(extractEndMarker);

      // Extract the parts
      const beforeExtract = content.substring(0, startIndex).trim();
      const extractText = content.substring(startIndex + extractStartMarker.length, endIndex).trim();
      const afterExtract = content.substring(endIndex + extractEndMarker.length).trim();

      // Parse question number and instruction from the first line
      const firstLine = beforeExtract.split('\n')[0] || '';
      const match = firstLine.match(/^(\d+)\s+(.*)/);

      return {
        hasExtract: true,
        questionNumber: match ? match[1] : '',
        extractInstruction: beforeExtract,
        extractText: extractText,
        mainQuestion: afterExtract,
        referencedLines,
      };
    }

    // If no extract markers found, return the whole content as main question
    return {
      hasExtract: false,
      questionNumber: '',
      extractInstruction: '',
      extractText: '',
      mainQuestion: content,
      referencedLines,
    };
  };

  // Update answer for current question with auto-save
  const handleAnswerChange = (value: string) => {
    const currentSlide = slides[currentSlideIndex];
    if (currentSlide && currentSlide.type === 'question' && attemptId) {
      const questionId = currentSlide.questionId;

      // Update local state immediately
      setAnswers((prev) => ({
        ...prev,
        [questionId]: value,
      }));

      // Clear existing timeout for this question
      if (saveTimeoutRef.current[questionId]) {
        clearTimeout(saveTimeoutRef.current[questionId]);
      }

      // Debounce the save - wait 1 second after user stops typing
      saveTimeoutRef.current[questionId] = setTimeout(() => {
        updateAnswer.mutate({
          attemptId,
          questionId,
          answerText: value,
        });
      }, 1000);
    }

    // Handle Section B answer
    if (currentSlide && currentSlide.type === 'section-b' && selectedSectionBQuestion && attemptId) {
      // Update local state immediately
      setAnswers((prev) => ({
        ...prev,
        [selectedSectionBQuestion]: value,
      }));

      // Clear existing timeout for this question
      if (saveTimeoutRef.current[selectedSectionBQuestion]) {
        clearTimeout(saveTimeoutRef.current[selectedSectionBQuestion]);
      }

      // Debounce the save - wait 1 second after user stops typing
      saveTimeoutRef.current[selectedSectionBQuestion] = setTimeout(() => {
        updateAnswer.mutate({
          attemptId,
          questionId: selectedSectionBQuestion,
          answerText: value,
        });
      }, 1000);
    }
  };

  // Handle Section B question selection - clear previous answer when switching
  const handleSectionBSelect = (questionId: string) => {
    if (!isTimeRemaining) return;

    // If switching to a different question, clear the previous answer
    if (selectedSectionBQuestion && selectedSectionBQuestion !== questionId) {
      // Clear the previous answer from local state
      setAnswers((prev) => {
        const newAnswers = { ...prev };
        delete newAnswers[selectedSectionBQuestion];
        return newAnswers;
      });

      // Clear any pending save timeout for the previous question
      if (saveTimeoutRef.current[selectedSectionBQuestion]) {
        clearTimeout(saveTimeoutRef.current[selectedSectionBQuestion]);
        delete saveTimeoutRef.current[selectedSectionBQuestion];
      }

      // Clear the previous answer in the database
      if (attemptId) {
        updateAnswer.mutate({
          attemptId,
          questionId: selectedSectionBQuestion,
          answerText: '',
        });
      }
    }

    setSelectedSectionBQuestion(questionId);
  };

  // Handle submit
  const handleSubmit = useCallback(async () => {
    if (!attemptId) return;

    try {
      // Clear all pending save timeouts and save immediately
      const savePromises = Object.entries(saveTimeoutRef.current).map(([questionId, timeout]) => {
        clearTimeout(timeout);
        delete saveTimeoutRef.current[questionId];
        const answerText = answers[questionId] || '';
        if (answerText) {
          return updateAnswer.mutateAsync({ attemptId, questionId, answerText });
        }
        return Promise.resolve();
      });

      // Wait for all answers to be saved
      await Promise.all(savePromises);

      // Subscribe to status changes using Realtime
      const subscription = supabaseClient
        .channel(`attempt-status-${attemptId}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'past_paper_attempts',
            filter: `id=eq.${attemptId}`,
          },
          (payload) => {
            console.log('Attempt status changed:', payload.new);
            const newStatus = (payload.new as { status: string }).status;

            // When status changes from in_progress to grading, navigate
            if (newStatus === 'grading' || newStatus === 'graded') {
              console.log('Status changed to grading/graded, navigating to results');
              subscription.unsubscribe();
              setShowSubmitModal(false);
              navigate(`/paper-attempts/${attemptId}/results`);
            }
          }
        )
        .subscribe();

      // Trigger submission (background process)
      submitAttempt.mutate({ attemptId });

      // Safety timeout - navigate after 10 seconds if no status change
      setTimeout(() => {
        console.warn('Status check timeout, navigating anyway');
        subscription.unsubscribe();
        setShowSubmitModal(false);
        navigate(`/paper-attempts/${attemptId}/results`);
      }, 5000);
    } catch (error) {
      console.error('Failed to submit attempt:', error);
      // Keep modal open so user can try again
    }
  }, [answers, attemptId, submitAttempt, updateAnswer, navigate]);

  // Handle next button
  const handleNext = () => {
    if (swiperRef.current) {
      if (currentSlideIndex < slides.length - 1) {
        swiperRef.current.slideNext();
      } else {
        // Last slide - enter review mode
        setIsReviewMode(true);
      }
    }
  };

  // Handle time up - auto submit
  const handleTimeUp = useCallback(async () => {
    if (!attemptId) return;

    // Show the modal immediately
    setShowTimeUpModal(true);

    try {
      // Auto-submit the attempt with whatever has been saved
      await submitAttempt.mutateAsync({ attemptId });

      // After submission completes, automatically navigate to results
      // Small delay so user can see the "Time's Up" message
      setTimeout(() => {
        navigate(`/paper-attempts/${attemptId}/results`);
      }, 1500);
    } catch (error) {
      console.error('Failed to submit attempt:', error);
      // Keep modal open, user can try clicking "View Results" button
    }
  }, [attemptId, submitAttempt, navigate]);

  // Handle back button
  const handleBack = useCallback(() => {
    if (isReviewMode) {
      setIsReviewMode(false);
    } else if (swiperRef.current && currentSlideIndex > 0) {
      swiperRef.current.slidePrev();
    }
  }, [currentSlideIndex, isReviewMode]);

  // Jump to specific question from review mode
  const handleJumpToQuestion = useCallback(
    (questionId: string) => {
      console.log('Jumping to question:', questionId);

      // Check if this question belongs to a choice section (like Section B)
      const question = questions.find((q) => q.id === questionId);
      const isChoiceSectionQuestion = paperConfig?.sections.some(
        (s) => s.hasQuestionChoice && s.categories.includes(question?.category as QuestionCategory)
      );

      if (isChoiceSectionQuestion) {
        // Find the section-b slide (choice section)
        const slideIndex = slides.findIndex((slide) => slide.type === 'section-b');
        console.log('Choice section slide index:', slideIndex);

        if (slideIndex !== -1 && swiperRef.current) {
          setIsReviewMode(false);
          // Use setTimeout to ensure state update completes before sliding
          setTimeout(() => {
            if (swiperRef.current) {
              swiperRef.current.slideTo(slideIndex);
            }
          }, 0);
        }
      } else {
        // Find the slide index for this question
        const slideIndex = slides.findIndex((slide) => slide.type === 'question' && slide.questionId === questionId);

        console.log('Question slide index:', slideIndex, 'for questionId:', questionId);
        console.log(
          'All slides:',
          slides.map((s, i) => ({ index: i, type: s.type, id: s.type === 'question' ? s.questionId : null }))
        );

        if (slideIndex !== -1 && swiperRef.current) {
          setIsReviewMode(false);
          // Use setTimeout to ensure state update completes before sliding
          setTimeout(() => {
            if (swiperRef.current) {
              swiperRef.current.slideTo(slideIndex);
            }
          }, 0);
        }
      }
    },
    [slides, questions, paperConfig]
  );

  // Check if time is remaining
  const isTimeRemaining = useMemo(() => {
    if (!attempt?.started_at || !paper?.time_allowed) return true;

    const startedAt = new Date(attempt.started_at).getTime();
    const now = Date.now();
    const elapsedSeconds = Math.floor((now - startedAt) / 1000);
    const totalSeconds = paper.time_allowed * 60;
    const remaining = totalSeconds - elapsedSeconds;

    return remaining > 0;
  }, [attempt?.started_at, paper?.time_allowed]);

  // Handle final submit from review mode
  const handleFinalSubmit = useCallback(() => {
    if (!isTimeRemaining) {
      // Time has expired - auto submit
      handleSubmit();
    } else {
      // Show confirmation modal
      setShowSubmitModal(true);
    }
  }, [isTimeRemaining, handleSubmit]);

  // Get header title based on current slide
  const getHeaderTitle = (): string => {
    if (isReviewMode) return 'Review Answers';
    if (!paper) return 'Paper not found';
    return paper.title || 'Past Paper';
  };

  // Get question counter text
  const getQuestionCounter = (): string => {
    const currentSlide = slides[currentSlideIndex];
    if (!paperConfig) return '';

    if (currentSlide && currentSlide.type === 'question') {
      // Find which section this question belongs to
      const questionCategory = currentSlide.question.category as QuestionCategory;
      const sectionConfig = paperConfig.sections.find((s) =>
        s.categories.includes(questionCategory)
      );

      if (sectionConfig) {
        // Count questions in this section
        const sectionQuestions = questions.filter((q) =>
          sectionConfig.categories.includes(q.category as QuestionCategory)
        );

        // Derive display label from category (e.g. Q7A → "7a", Q7B → "7b", Q1 → "1")
        const categoryMatch = (questionCategory as string).match(/^Q(\d+)([A-Z]+)$/);
        if (categoryMatch && categoryMatch[2]) {
          // Compound question like Q7A/Q7B — show "7a" / "7b" without total
          const qLabel = `${categoryMatch[1]}${categoryMatch[2].toLowerCase()}`;
          return `Section ${sectionConfig.id} - Question ${qLabel}`;
        }

        const questionNumInSection = currentSlide.questionIndex + 1;
        return `Section ${sectionConfig.id} - Question ${questionNumInSection}/${sectionQuestions.length}`;
      }
    }
    if (currentSlide && currentSlide.type === 'section-b') {
      // Find the section config for choice questions
      const choiceSection = paperConfig.sections.find((s) => s.hasQuestionChoice);
      return `Section ${choiceSection?.id || 'B'} - Choose ONE question`;
    }
    return '';
  };

  const getContentMessage = (): string => {
    if (!paper) return 'This paper does not exist.';
    if (isLoadingQuestions) return 'Loading questions...';
    if (questions.length === 0) return 'No questions available for this paper.';
    return '';
  };

  const showExamContent = paper && !isLoadingQuestions && questions.length > 0 && slides.length > 0;

  // Determine if we're on the last slide
  const isLastSlide = currentSlideIndex === slides.length - 1;
  const currentSlide = slides[currentSlideIndex];
  const isOnQuestion = currentSlide && currentSlide.type === 'question';

  return (
    <>
      <PageHeader
        title={paper ? formatPastPaperTitle(paper) : getHeaderTitle()}
        showBackButton
        onBackClick={handleBack}
        disableBackButton={!isReviewMode && currentSlideIndex === 0}
        titleAlign={
          !paper || isLoadingAttempt || isLoadingQuestions || questions.length === 0
            ? 'left'
            : isReviewMode
              ? 'left'
              : 'right'
        }
        className={showExamContent ? 'text-xl' : ''}
        attemptStartedAt={attempt?.started_at}
        timeAllowedMinutes={paper?.time_allowed}
        onTimeUp={handleTimeUp}
        rightContent={attemptId && !isReviewMode && <ExitPaperAttemptButton attemptId={attemptId} />}
      >
        {isReviewMode && (
          <div className="flex gap-4 ml-auto">
            <Button
              size="md"
              onClick={handleFinalSubmit}
              className="bg-orange-500 hover:bg-orange-600 border-none text-white px-8"
              disabled={!isTimeRemaining}
            >
              Submit Paper
            </Button>
          </div>
        )}
      </PageHeader>

      {/* Error/Loading States */}
      {!showExamContent && (
        <div className="flex items-center justify-center flex-1">
          <p className="text-gray-500">{getContentMessage()}</p>
        </div>
      )}

      {/* Review Mode Summary */}
      {showExamContent && isReviewMode && paperConfig && (
        <div className="flex-1 flex flex-col gap-5 p-6 overflow-y-auto">
          {/* Non-choice section questions (e.g., Section A) */}
          {paperConfig.sections
            .filter((s) => !s.hasQuestionChoice)
            .flatMap((sectionConfig) =>
              questions
                .filter((q) => sectionConfig.categories.includes(q.category as QuestionCategory))
                .map((question, index) => (
                  <button
                    key={question.id}
                    onClick={() => handleJumpToQuestion(question.id)}
                    className="w-full p-6 bg-white rounded-[20px] border-[0.72px] border-[#CCCCCC] border-opacity-50 text-left hover:shadow-md transition-shadow"
                  >
                    <span className="text-lg font-medium">
                      <span className="text-text-primary">Question {index + 1}</span>
                      {answers[question.id] ? (
                        <span className="text-green-600 ml-2">✓ Answered</span>
                      ) : (
                        <span className="text-orange-600 ml-2">Not answered</span>
                      )}
                    </span>
                  </button>
                ))
            )}

          {/* Choice section question (e.g., Section B) */}
          {selectedSectionBQuestion && (
            <button
              onClick={() => handleJumpToQuestion(selectedSectionBQuestion)}
              className="w-full p-6 bg-white rounded-[20px] border-[0.72px] border-[#CCCCCC] border-opacity-50 text-left hover:shadow-md transition-shadow"
            >
              <span className="text-lg font-medium">
                <span className="text-text-primary">
                  {paperConfig.sections.find((s) => s.hasQuestionChoice)?.title || 'Section B'}
                </span>
                {answers[selectedSectionBQuestion] ? (
                  <span className="text-green-600 ml-2">✓ Answered</span>
                ) : (
                  <span className="text-orange-600 ml-2">Not answered</span>
                )}
              </span>
            </button>
          )}

          {/* Time Warning */}
          {!isTimeRemaining && (
            <div className="bg-red-50 border-2 border-red-400 rounded-[20px] p-6">
              <p className="text-red-700 font-semibold">
                ⚠️ Time has expired. You can no longer edit your answers. Please submit your paper.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Main Exam Content with Swiper */}
      {showExamContent && !isReviewMode && (
        <div className="flex-1 flex flex-col gap-6 overflow-hidden">
          {/* Question Counter and Reading Insert Button */}
          <div className="flex-shrink-0 flex items-center gap-6">
            <p className="text-lg font-medium text-gray-700">{getQuestionCounter()}</p>

            {/* View Reading Insert Button - Only show for questions with reading insert */}
            {isOnQuestion &&
              currentSlide.type === 'question' &&
              paperConfig?.readingInsert &&
              paper.reading_insert_page_numbers &&
              paper.reading_insert_page_numbers.length > 0 && (() => {
                const ri = paperConfig.readingInsert!;
                const cat = currentSlide.question.category as QuestionCategory;
                if (ri.textCount === 1) {
                  return (
                    <ReadingInsertButton
                      label={ri.buttonLabels[0]}
                      onClick={() => setShowReadingInsert(true)}
                    />
                  );
                }
                // Dual-text paper: show buttons based on which text(s) this question uses
                const isDual = ri.dualViewCategories?.includes(cat);
                const isText2Only = ri.text2Categories?.includes(cat);
                if (isDual) {
                  return (
                    <div className="flex gap-2">
                      <ReadingInsertButton label={ri.buttonLabels[0]} onClick={() => setShowReadingInsertText1(true)} />
                      <ReadingInsertButton label={ri.buttonLabels[1]} onClick={() => setShowReadingInsertText2(true)} />
                    </div>
                  );
                }
                if (isText2Only) {
                  return (
                    <ReadingInsertButton label={ri.buttonLabels[1]} onClick={() => setShowReadingInsertText2(true)} />
                  );
                }
                // Default: Text 1 only
                return (
                  <ReadingInsertButton label={ri.buttonLabels[0]} onClick={() => setShowReadingInsertText1(true)} />
                );
              })()}
          </div>

          {/* Swiper Container */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <Swiper
              modules={[Navigation]}
              spaceBetween={0}
              slidesPerView={1}
              onSwiper={(swiper) => (swiperRef.current = swiper)}
              onSlideChange={handleSlideChange}
              allowTouchMove={false} // Disable swipe, use buttons only
              className="w-full h-full"
            >
              {slides.map((slide, index) => (
                <SwiperSlide key={index} className="h-full overflow-y-auto">
                  {slide.type === 'section-intro' ? (
                    // Section Introduction Slide
                    <div className="flex-1 flex items-center justify-center">
                      <div className="bg-white rounded-lg shadow-sm p-12 max-w-3xl text-center space-y-6">
                        <h2 className="text-4xl font-bold text-gray-900">{slide.title}</h2>
                        <h3 className="text-2xl font-semibold text-gray-700">{slide.subtitle}</h3>
                        <div className="space-y-3 pt-4">
                          {slide.instructions.map((instruction, idx) => (
                            <p key={idx} className="text-lg text-gray-600">
                              {instruction}
                            </p>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : slide.type === 'section-b' ? (
                    // Section B - Multiple choice questions
                    <div className="flex flex-col gap-6 p-1 min-h-full">
                      {/* Question Options */}
                      <div className="bg-white rounded-lg shadow-sm overflow-hidden transition-all flex-shrink-0">
                        <div
                          className={`p-6 ${isQuestionCollapsed ? '' : 'max-h-[60vh] overflow-y-auto'} flex flex-col gap-6 relative`}
                        >
                          {/* Collapse/Expand Button */}
                          <button
                            onClick={() => setIsQuestionCollapsed(!isQuestionCollapsed)}
                            className="absolute top-4 right-4 w-12 h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-full flex items-center justify-center shadow-lg transition-colors z-10"
                            title={isQuestionCollapsed ? 'Expand questions' : 'Collapse questions'}
                          >
                            {isQuestionCollapsed ? (
                              <ChevronDownIcon className="w-10 h-10" />
                            ) : (
                              <ChevronUpIcon className="w-10 h-10" />
                            )}
                          </button>

                          {/* Question Text */}
                          <div className="flex flex-col gap-4 pr-14">
                            {isQuestionCollapsed ? (
                              <p className="text-base leading-relaxed whitespace-pre-wrap line-clamp-2">
                                Choose ONE of the following questions to answer
                              </p>
                            ) : (
                              <>
                                <p className="text-base font-semibold text-gray-700 mb-4">
                                  Choose ONE of the following questions to answer:
                                </p>

                                {slide.questions.map((question) => (
                                  <div
                                    key={question.id}
                                    className={`border-2 rounded-lg p-6 transition-all ${
                                      selectedSectionBQuestion === question.id
                                        ? 'border-orange-500 bg-orange-50'
                                        : 'border-gray-300 hover:border-orange-300'
                                    } ${!isTimeRemaining ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                    onClick={() => handleSectionBSelect(question.id)}
                                  >
                                    <div className="flex items-start gap-4">
                                      <div className="flex-shrink-0 mt-1">
                                        <div
                                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                            selectedSectionBQuestion === question.id
                                              ? 'border-orange-500 bg-orange-500'
                                              : 'border-gray-400'
                                          }`}
                                        >
                                          {selectedSectionBQuestion === question.id && (
                                            <div className="w-2 h-2 bg-white rounded-full" />
                                          )}
                                        </div>
                                      </div>
                                      <div className="flex-1">
                                        <div className="text-base leading-relaxed">
                                          <MarkdownContent content={question.content || ''} />
                                        </div>
                                        <span className="text-sm font-bold text-gray-600 mt-2 inline-block">
                                          [{question.marks} marks]
                                        </span>

                                        {/* Display images if question has them */}
                                        {question.image_page_numbers &&
                                          question.image_page_numbers.length > 0 &&
                                          pdfUrl && (
                                            <div className="flex flex-col gap-2 mt-4">
                                              <p className="text-sm font-medium text-gray-600">Reference Image(s):</p>
                                              <Document file={pdfUrl}>
                                                {question.image_page_numbers.map((pageNum: number) => (
                                                  <div key={pageNum} className="mb-4">
                                                    <Page
                                                      pageNumber={pageNum}
                                                      width={Math.min(window.innerWidth * 0.5, 500)}
                                                      renderTextLayer={false}
                                                      renderAnnotationLayer={false}
                                                      className="shadow-md"
                                                    />
                                                  </div>
                                                ))}
                                              </Document>
                                            </div>
                                          )}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Answer Input */}
                      {selectedSectionBQuestion && (
                        <div className="flex-1 flex flex-col gap-2 pb-4">
                          <div className="flex items-center justify-between">
                            <label className="text-sm font-medium text-gray-700">Your Answer:</label>
                            <span className="text-xs text-gray-500">
                              {getCurrentAnswer().length} / 4,500 characters
                            </span>
                          </div>
                          {getCurrentAnswer().length >= 4500 && (
                            <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3 text-sm text-yellow-800">
                              <strong>Character limit exceeded</strong> — shorten your response while keeping key points to save exam time
                            </div>
                          )}
                          <textarea
                            value={getCurrentAnswer()}
                            onChange={(e) => handleAnswerChange(e.target.value)}
                            placeholder="Type your answer here..."
                            className="flex-1 w-full p-4 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-orange-500 resize-none"
                            disabled={!isTimeRemaining}
                            maxLength={4500}
                          />
                        </div>
                      )}
                    </div>
                  ) : slide.type === 'question' ? (
                    // Regular Question Slide
                    <div className="flex flex-col gap-6 p-1 min-h-full">
                      <QuestionContentArea
                        questionContent={slide.question.content || ''}
                        marks={slide.question.marks || 0}
                        imagePageNumbers={slide.question.image_page_numbers}
                        pdfUrl={pdfUrl}
                        readingInsertContent={paper?.reading_insert_content || undefined}
                        isCollapsed={isQuestionCollapsed}
                        onToggleCollapse={() => setIsQuestionCollapsed(!isQuestionCollapsed)}
                        parseQuestionContent={parseQuestionContent}
                      />

                      {/* Answer Input */}
                      <div className="flex-1 flex flex-col gap-2 pb-4">
                        <div className="flex items-center justify-between">
                          <label className="text-sm font-medium text-gray-700">Your Answer:</label>
                          <span className="text-xs text-gray-500">{getCurrentAnswer().length} / 4,500 characters</span>
                        </div>
                        {getCurrentAnswer().length >= 4500 && (
                          <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3 text-sm text-yellow-800">
                            <strong>Character limit exceeded</strong> — shorten your response while keeping key points to save exam time
                          </div>
                        )}
                        <textarea
                          value={getCurrentAnswer()}
                          onChange={(e) => handleAnswerChange(e.target.value)}
                          placeholder="Type your answer here..."
                          className="flex-1 w-full p-4 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-orange-500 resize-none"
                          disabled={!isTimeRemaining}
                          maxLength={4500}
                        />
                      </div>
                    </div>
                  ) : null}
                </SwiperSlide>
              ))}
            </Swiper>
          </div>

          {/* Next/Submit Buttons */}
          <div className="flex-shrink-0 flex justify-end items-end gap-4">
            <div className="flex flex-col items-end gap-2">
              <Button
                color="primary"
                onClick={handleNext}
                className="bg-primary hover:bg-dark-hover-secondary border-none text-white px-8"
              >
                {isLastSlide ? 'Submit for Marking' : 'Next'}
              </Button>
              {isLastSlide && <p className="text-sm text-gray-500">You will see feedback after you submit.</p>}
            </div>
          </div>
        </div>
      )}

      {/* Reading Insert Modal */}
      {showExamContent && pdfUrl && paper.reading_insert_page_numbers && (
        <>
          <ReadingInsertModal
            isOpen={showReadingInsert}
            onClose={() => setShowReadingInsert(false)}
            pdfUrl={pdfUrl}
            pageNumbers={paper.reading_insert_page_numbers}
          />
          {/* Dual reading insert modals for side-by-side text viewing (used by synthesis questions) */}
          {paperConfig?.readingInsert && paperConfig.readingInsert.textCount === 2 && (
            <>
              <ReadingInsertModal
                isOpen={showReadingInsertText1}
                onClose={() => setShowReadingInsertText1(false)}
                pdfUrl={pdfUrl}
                pageNumbers={paper.reading_insert_page_numbers}
                title={paperConfig.readingInsert.buttonLabels[0]?.replace('View ', '') || 'Text 1'}
                defaultPosition="left"
              />
              <ReadingInsertModal
                isOpen={showReadingInsertText2}
                onClose={() => setShowReadingInsertText2(false)}
                pdfUrl={pdfUrl}
                pageNumbers={paper.reading_insert_page_numbers_2 ?? paper.reading_insert_page_numbers}
                title={paperConfig.readingInsert.buttonLabels[1]?.replace('View ', '') || 'Text 2'}
                defaultPosition="right"
              />
            </>
          )}
        </>
      )}

      {/* Time's Up Modal */}
      {showTimeUpModal && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black bg-opacity-50 z-[9998]" />

          {/* Modal */}
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl shadow-xl max-w-2xl w-full p-12">
              <h2 className="text-4xl font-bold mb-6">Time's Up!</h2>
              <p className="text-lg text-gray-700 mb-2">Your time for this paper has ended.</p>
              <p className="text-lg text-gray-700 mb-8">
                Your answers have been automatically submitted for marking. You'll now be taken to the Feedback summary,
                where you can review your results and notes.
              </p>
              <Button
                size="lg"
                className="w-full bg-[#2B3440] hover:bg-dark-hover-secondary border-none text-white text-lg py-4"
                onClick={() => {
                  // Only allow navigation if submission is complete
                  if (!submitAttempt.isPending) {
                    navigate(`/paper-attempts/${attemptId}/results`);
                  }
                }}
                loading={submitAttempt.isPending}
                disabled={submitAttempt.isPending}
              >
                {submitAttempt.isPending ? 'Submitting...' : 'View Results'}
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Submit Confirmation Modal */}
      {showSubmitModal && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black bg-opacity-50 z-[9998]" />

          {/* Modal */}
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl shadow-xl max-w-2xl w-full p-12">
              <h2 className="text-4xl font-bold mb-6">Submit answer</h2>
              <p className="text-lg text-gray-700 mb-8">
                Are you sure you want to proceed to the summary? You can still review your answers before the final
                assessment. Your Feedback, will only be visible after completing the test.
              </p>
              <div className="flex gap-4">
                <Button
                  size="lg"
                  variant="outline"
                  className="flex-1 border-2 border-gray-900 text-gray-900 hover:bg-gray-100 text-lg py-4"
                  onClick={() => setShowSubmitModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  size="lg"
                  className="flex-1 bg-[#2B3440] hover:bg-dark-hover-secondary border-none text-white text-lg py-4"
                  onClick={handleSubmit}
                  loading={submitAttempt.isPending}
                >
                  Submit
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Navigation Warning Modal */}
      {showNavigationWarning && blocker.state === 'blocked' && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black bg-opacity-50 z-[9998]" />

          {/* Modal */}
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl shadow-xl max-w-2xl w-full p-12">
              <h2 className="text-4xl font-bold mb-6">Leave Exam?</h2>
              <p className="text-lg text-gray-700 mb-2">You are currently taking an exam.</p>
              <p className="text-lg text-gray-700 mb-8">Are you sure you want to leave?</p>
              <div className="flex gap-4">
                <Button
                  size="lg"
                  variant="outline"
                  className="flex-1 border-2 border-gray-900 text-gray-900 hover:bg-gray-100 text-lg py-4"
                  onClick={() => {
                    setShowNavigationWarning(false);
                    blocker.reset();
                  }}
                >
                  Stay on Exam
                </Button>
                <Button
                  size="lg"
                  className="flex-1 bg-red-600 hover:bg-red-700 border-none text-white text-lg py-4"
                  onClick={() => {
                    setShowNavigationWarning(false);
                    blocker.proceed();
                  }}
                >
                  Leave Exam
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default TakePaperPage;
